import { useEffect, useRef, useState } from 'react'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import headerIcon from '#/assets/header-icon.svg'

import { PILLARS } from './data'
import { SectionDecor } from './SectionDecor'
import { PillarRow } from './PillarRow'
import { PillarDotNav } from './PillarDotNav'

export default function AboutPillarsSection() {
  const [activeIndex, setActiveIndex] = useState(-1)
  const [navVisible, setNavVisible] = useState(false)

  const sectionRef = useRef<HTMLElement | null>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current
      if (!section) return

      const vh = window.innerHeight
      const { top, bottom } = section.getBoundingClientRect()

      // Clear active state when section is fully off screen
      if (bottom < 0 || top > vh) {
        setActiveIndex(-1)
        setNavVisible(false)
        return
      }

      // Pick the row whose vertical midpoint is closest to the viewport midpoint
      const mid = vh / 2
      let best = -1
      let bestDist = Infinity

      rowRefs.current.forEach((el, idx) => {
        if (!el) return
        const rect = el.getBoundingClientRect()
        const rowMid = rect.top + rect.height / 2
        const dist = Math.abs(rowMid - mid)
        if (dist < bestDist) {
          bestDist = dist
          best = idx
        }
      })

      setActiveIndex(best)

      /**
       * Nav visibility: use the ROWS' own bounds, NOT the section bounds.
       * The section has py-28 (~112px) padding that keeps section.bottom
       * artificially high — causing the nav to linger in the next section.
       *
       * Instead: show nav only while the actual row content spans the
       * viewport midpoint (firstRow.top ≤ mid AND lastRow.bottom ≥ mid).
       */
      const firstRow = rowRefs.current[0]
      const lastRow = rowRefs.current[rowRefs.current.length - 1]
      if (firstRow && lastRow) {
        const firstTop = firstRow.getBoundingClientRect().top
        const lastBottom = lastRow.getBoundingClientRect().bottom
        setNavVisible(firstTop <= mid && lastBottom >= mid)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToPillar = (idx: number) =>
    rowRefs.current[idx]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })

  return (
    <>
      {/* Dot nav is rendered outside <section> so it can't be clipped */}
      <PillarDotNav
        pillars={PILLARS}
        activeIndex={activeIndex}
        visible={navVisible}
        onDotClick={scrollToPillar}
      />

      {/*
        overflow-hidden intentionally omitted — it breaks position:sticky.
        Decorative elements are clipped inside SectionDecor's own wrapper.
      */}
      <section
        ref={sectionRef}
        className="relative w-full py-20 sm:py-28 noir-section"
        id="about-pillars"
      >
        <SectionDecor />

        <div className="container mx-auto flex flex-col gap-16">
          {/* Section header */}
          <ScrollReveal className="flex flex-col items-center gap-4 text-center max-w-2xl mx-auto">
            <RevealItem>
              <img
                alt=""
                src={headerIcon}
                className="brightness-0 invert opacity-60"
              />
            </RevealItem>
            <RevealItem>
              <p className="text-sm font-heading font-semibold uppercase tracking-[0.3em] text-primary-300">
                WHAT DRIVES US
              </p>
            </RevealItem>
            <RevealItem className="w-full">
              <h2 className="text-4xl font-bold text-white sm:text-5xl">
                <TextAnimate by="word" animation="blurInUp" as="span">
                  Our
                </TextAnimate>{' '}
                <HighlightText
                  inView
                  text="Mission"
                  className="bg-linear-to-r from-primary-600/60 to-primary-800/60 text-white"
                />
                , Vision & Core Values
              </h2>
            </RevealItem>
            <RevealItem>
              <TextAnimate
                by="word"
                animation="fadeIn"
                duration={1.1}
                className="text-base text-neutral-300 sm:text-lg"
              >
                We are committed to making Quran learning accessible,
                meaningful, and inspiring for every learner, wherever they are
                in the world.
              </TextAnimate>
            </RevealItem>
          </ScrollReveal>

          {/* Pillar rows */}

          <div className="flex flex-col mx-auto 2xl:max-w-full xl:max-w-5xl lg:max-w-3xl">
            <ScrollReveal stagger={0.4}>
              {PILLARS.map((pillar, idx) => (
                <RevealItem key={pillar.label}>
                  <PillarRow
                    pillar={pillar}
                    index={idx}
                    total={PILLARS.length}
                    isActive={activeIndex === idx}
                    isDimmed={activeIndex !== -1 && activeIndex !== idx}
                    rowRef={(el) => {
                      rowRefs.current[idx] = el
                    }}
                  />
                </RevealItem>
              ))}
            </ScrollReveal>
          </div>
        </div>
      </section>
    </>
  )
}
